// @flow

import { Agent } from 'https'
import axios, { AxiosRequestConfig } from 'axios'
import ErrorManager from './ErrorManager'

/**
 *
 * Main connector to DMP centralizing main communication process
 ** Transactions list at : https://bit.ly/3mQyVOx
 * @class MainConnector
 */
class MainConnector {
    wsUrl: any

    gatewayUrl: any

    mainParams: any

    verbose: any

    testEnv: any

    sessionToken: any

    /**
     * Default constructor
     * @param {boolean} verbose = if true verbose
     * @param {boolean} testEnv = if true test env (eg. not real life DMP)
     * @param {string} sessionToken the session token of current user
     */
    constructor(verbose = false, testEnv = false, sessionToken = '') {
        this.wsUrl = null
        this.gatewayUrl = null
        this.mainParams = {}
        this.mainParams.i_timeout = 30
        this.verbose = verbose
        this.testEnv = testEnv
        this.sessionToken = sessionToken
    }

    /**
     * Add EsUser object to connector
     */
    setEsUser(esUser: any) {
        this.mainParams.EsUser = esUser.EsUser
    }

    /**
     * Add Url for dmpconnect es rest webservice
     */
    setRestWsURl(wsUrl: string) {
        this.wsUrl = wsUrl
    }

    /**
     * Add s_esId to connector
     */
    setEsId(esId: string) {
        this.mainParams.s_esid = esId
        this.mainParams.s_esId = esId
    }

    /**
     * Add s_dmpUrl to connector
     */
    setDmpUrl(dmpUrl: string) {
        this.mainParams.s_dmpUrl = dmpUrl
    }

    /**
     * @description Set the gateway url to acces the dmpconnect-es-rest service
     * @param {string} gateWayUrl must be defined if useGateway is true
     * @returns {void}
     */
    setGatewayUrl(gateWayUrl: string) {
        this.gatewayUrl = gateWayUrl
    }

    isInitialized = async () => {
        const finalUrl = `${this.wsUrl}isInitialized`
        return this.postRequest(finalUrl, {})
    }

    /**
     * Sends a request to DMP for a specific endPoint and with extra parameters for it
     * (default EsUser, s_dmpUrl and s_esId are automatically added)
     * @returns {Promise<Object>}
     */
    makeRequest(endPointName: string, endPointExtraParams: any): Promise<any> {
        return new Promise((resolve, reject) => {
            // check that ws url is set
            if (!this.wsUrl && !this.gatewayUrl) {
                ErrorManager.sendError(
                    `cannot make request because neither wsUrl nor gatewayUrl are defined (endPoint was : ${endPointName})`,
                )
            }

            // check that necessary params are present
            if (!this.mainParams.EsUser || !this.mainParams.s_esId || !this.mainParams.s_dmpUrl) {
                ErrorManager.sendError(
                    `cannot make request missing EsUser, s_esId or s_dmpUrl in parameters ! (endPoint was : ${endPointName})`,
                )
            }

            if (!endPointExtraParams) {
                ErrorManager.sendError(
                    `cannot make request missing extra parameters for specific endPoint (endPoint was : ${endPointName})`,
                )
            }

            const finalParams = {
                ...this.mainParams,
                ...endPointExtraParams,
            }
            let finalUrl = this.wsUrl + endPointName
            if (this.gatewayUrl) finalUrl = this.gatewayUrl + endPointName

            // display log if needed
            if (this.verbose) {
                console.log(`Making DMP request for final url ${finalUrl} with paramrs ${JSON.stringify(finalParams)}`)
            }

            this.postRequest(finalUrl, finalParams).then((e) => resolve(e)).catch((e) => reject(e))
        })
    }

    private postRequest = (finalUrl :string, finalParams : any) => new Promise((resolve, reject) => {
        const axiosConfig: AxiosRequestConfig = {}
        if (this.testEnv && !this.gatewayUrl) {
            // For ssl bypass
            axiosConfig.httpsAgent = new Agent({
                rejectUnauthorized: false,
            })
        }
        if (this.sessionToken) {
            axiosConfig.headers = {
                'X-Session-Token': this.sessionToken,
            }
        }
        const instance = axios.create(axiosConfig)

        instance
            .post(finalUrl, finalParams)
            .then((response: any) => {
                resolve(response.data)
            })
            .catch((error: any) => {
                if (error.response) {
                    // The request was made and the server responded with a status code that falls out of the range of 2xx
                    resolve(error.response.data)
                } else {
                    // Something happened in setting up the request that triggered an Error
                    reject('Something happened in setting up the request that triggered an Error')
                }
                // if (errorData.response) { reject(`An error occured with making request : code - ${errorData.response.data.i_apiErrorCode} message - ${errorData.response.data.s_apiErrorExtendedInformations}`) } else { reject(`An error occured with making request : ${errorData}`) }
            })
    })
}

export default MainConnector
